import * as React from "react";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridSortModel,
  GridRowModel,
  GridRowClassNameParams,
  GridValueGetterParams,
} from "@mui/x-data-grid";
import { Stack, Link, useMediaQuery, useTheme } from "@mui/material";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import NoResultsOverlay from "../../components/NoResultOverlay";
import { toast } from "react-toastify";
import useDebounce from "../../hooks/useDebounce";
import { useTitle } from "../../hooks/useTitle";
import {
  useDeleteUserFeedbackMutation,
  useGetUserFeedbacksQuery,
} from "../../services/feedback.service";
import { UserFeedback } from "../../types/feedback";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { Download as DownloadIcon } from "@mui/icons-material";
import { CardBase, GlassDataGrid, StyledTextField, Button, OrangeLinearProgress } from "../../components/global";

const UserFeedbacksPage = () => {
  useTitle("User Feedbacks");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [deleteModal, setDeleteModal] = React.useState<{
    open: boolean;
    row: GridRowModel<UserFeedback> | null;
  }>({
    open: false,
    row: null,
  });
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(25);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);

  React.useEffect(() => {
    setSearchQuery(debounceQuery);
  }, [debounceQuery]);

  const { data, isLoading, isFetching } = useGetUserFeedbacksQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    search: searchQuery,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const [doDelete, { isLoading: deleting }] = useDeleteUserFeedbackMutation();

  const handleDeleteRow = React.useCallback((row: GridRowModel<UserFeedback>) => {
    setDeleteModal({ open: true, row });
  }, []);

  const rows: GridRowsProp | undefined = React.useMemo(
    () => data?.data,
    [data]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      { field: "id", width: 50 },
      {
        field: "created_at",
        headerName: "Time",
        minWidth: isMobile ? 120 : 150,
        valueGetter: (params: GridValueGetterParams<undefined, UserFeedback>) =>
          `${dayjs(params.row?.created_at).format("MMM DD, hh:mm A")}`,
      },
      {
        field: "user",
        headerName: "Sender",
        minWidth: isMobile ? 120 : 150,
        renderCell: (params: GridRenderCellParams<undefined, UserFeedback>) => (
          <Link
            onClick={() => navigate(`/talents/${params.row.user?.id}`)}
            component="button"
            variant="body2"
            sx={{
              color: "#F4A259",
              fontWeight: 600,
              textDecoration: "none",
              fontSize: isMobile ? "0.75rem" : "0.875rem",
              "&:hover": {
                textDecoration: "underline",
                color: "#E8944A",
              },
            }}
          >
            #{params.row.user?.id} {params.row.user?.full_name}
          </Link>
        ),
      },
      {
        field: "message",
        headerName: "Message",
        flex: 1,
        minWidth: isMobile ? 150 : 200,
      },
      {
        field: "type",
        headerName: "Type",
        minWidth: isMobile ? 100 : 150,
      },
      {
        field: "info",
        headerName: "Info",
        minWidth: isMobile ? 150 : 300,
        flex: 1,
      },
    ],
    [isMobile]
  );

  const closeDeleteModal = () =>
    setDeleteModal({ ...deleteModal, open: false });

  const handleDelete = async () => {
    if (deleteModal.row?.id) {
      try {
        await doDelete(deleteModal.row.id).unwrap();
        closeDeleteModal();
        toast.success("Successfully deleted");
      } catch (err) {
        closeDeleteModal();
      }
    }
  };

  const handleExport = React.useCallback(() => {
    if (!rows || rows.length === 0) return;

    // Get visible columns (excluding control column)
    const visibleColumns = columns.filter((col) => col.field !== "control" && !col.hide);

    // Create CSV headers
    const headers = visibleColumns.map((col) => `"${col.headerName || col.field}"`).join(",");

    // Create CSV rows
    const csvRows = rows.map((row: any) => {
      return visibleColumns
        .map((col) => {
          let value: string = "";
          if (col.valueGetter) {
            const params = { row } as any;
            const result = col.valueGetter(params);
            value = result != null ? String(result) : "";
          } else if (col.field) {
            const rawValue = (row as any)[col.field];
            value = rawValue ? String(rawValue) : "";
          }
          // Escape quotes and wrap in quotes if needed
          if (value && (value.includes(",") || value.includes('"') || value.includes("\n"))) {
            value = `"${value.replace(/"/g, '""')}"`;
          }
          return value || "";
        })
        .join(",");
    });

    // Combine header and rows
    const csvContent = [headers, ...csvRows].join("\n");

    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `User_Feedbacks_${dayjs().format("YYYY-MM-DD")}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [rows, columns]);

  const getRowClassName = (params: GridRowClassNameParams) => {
    return !!params.row.deleted_at ? "deleted" : "";
  };

  return (
    <>
      <CardBase
        title="User Feedbacks"
        useGlassCard={false}
        sx={{ height: isMobile ? "70vh" : "85vh", p: 0 }}
        headerActions={
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={1}
            alignItems={isMobile ? "stretch" : "center"}
            sx={{ width: isMobile ? "100%" : "auto" }}
          >
            <StyledTextField
              size="small"
              label="Enter to search.."
              value={query || ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
              sx={{ minWidth: isMobile ? "100%" : 250 }}
            />
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                width: isMobile ? "100%" : "auto",
                flexWrap: isMobile ? "wrap" : "nowrap",
              }}
            >
              {!!query && (
                <Button
                  variant="text"
                  onClick={() => setQuery("")}
                  size="small"
                  fullWidth={isMobile}
                  sx={{
                    color: "#F4A259",
                    "&:hover": {
                      backgroundColor: "rgba(244, 162, 89, 0.08)",
                    },
                  }}
                >
                  Reset
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExport}
                disabled={!rows || rows.length === 0}
                size="small"
                fullWidth={isMobile}
                sx={{
                  color: "#F4A259",
                  borderColor: "#F4A259",
                  fontWeight: 600,
                  "&:hover": {
                    borderColor: "#F4A259",
                    backgroundColor: "rgba(244, 162, 89, 0.08)",
                    transform: "translateY(-1px)",
                  },
                  "&:disabled": {
                    borderColor: "rgba(244, 162, 89, 0.5)",
                    color: "rgba(244, 162, 89, 0.5)",
                  },
                  transition: "all 0.2s ease",
                }}
              >
                {isMobile ? "Export" : "Export CSV"}
              </Button>
            </Stack>
          </Stack>
        }
      >
        <GlassDataGrid
          rows={rows || []}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          page={page}
          onPageChange={(newPage: number) => setPage(newPage)}
          pageSize={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onPageSizeChange={(newSize: number) => setPageSize(newSize)}
          sortModel={sortModel}
          sortingMode="server"
          onSortModelChange={(newSortModel: GridSortModel) => setSortModel(newSortModel)}
          loading={isLoading || isFetching}
          components={{
            LoadingOverlay: OrangeLinearProgress,
            NoResultsOverlay: NoResultsOverlay,
          }}
          disableSelectionOnClick
          disableColumnFilter
          density="compact"
          height={isMobile ? "calc(70vh - 80px)" : "calc(85vh - 80px)"}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "transparent",
              borderTop: "none",
              paddingLeft: isMobile ? 1.5 : 3,
              paddingRight: isMobile ? 1.5 : 3,
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "#F4A259",
                fontWeight: 600,
                fontSize: isMobile ? "0.75rem" : "0.875rem",
              },
              "& .MuiDataGrid-columnHeader": {
                color: "#F4A259",
                "& .MuiDataGrid-iconButtonContainer": {
                  color: "#F4A259",
                },
                "& .MuiDataGrid-sortIcon": {
                  color: "#F4A259",
                },
              },
            },
            "& .MuiDataGrid-cell": {
              paddingLeft: isMobile ? 1.5 : 3,
              paddingRight: isMobile ? 1.5 : 3,
              py: isMobile ? 1 : 2,
              fontSize: isMobile ? "0.75rem" : "0.875rem",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(244, 162, 89, 0.05)",
            },
            "& .deleted": {
              fontStyle: "italic",
              color: "#aaa",
            },
          }}
          getRowClassName={getRowClassName}
        />
      </CardBase>
      <ConfirmDeleteModal
        open={deleteModal.open}
        onClose={closeDeleteModal}
        onDelete={handleDelete}
        message={`Delete Feedback #${deleteModal.row?.id} ?`}
      />
    </>
  );
};
export default UserFeedbacksPage;
